import { useState } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SectionTitle from '@/components/base/SectionTitle';

const FORM_URL = 'https://readdy.ai/api/form/d9q8ikcnlsngrm4lltd0';

export default function AboutPage() {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [contactStatus, setContactStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [contactMessage, setContactMessage] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setContactStatus('success');
      setContactMessage('感谢你的留言，我们会尽快回复！');
      setContactForm({ name: '', email: '', subject: '', message: '' });
      return;
    }

    setContactStatus('loading');

    try {
      const payload = new URLSearchParams();
      payload.append('name', contactForm.name);
      payload.append('email', contactForm.email);
      payload.append('subject', contactForm.subject);
      payload.append('message', contactForm.message);

      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch (_err) { /* parse error */ }

      if (response.ok && parsed?.code === 'OK') {
        setContactStatus('success');
        setContactMessage('感谢你的留言，我们会尽快回复！');
        setContactForm({ name: '', email: '', subject: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setContactStatus('error');
        setContactMessage(serverMsg);
      }
    } catch {
      setContactStatus('error');
      setContactMessage('网络错误，请检查网络连接后重试');
    }
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '吃瓜网的内容来源是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '吃瓜网的内容来源于各大社交媒体、娱乐新闻报道以及网友投稿。我们会对信息进行整理和筛选，确保内容的真实性和时效性。',
        },
      },
      {
        '@type': 'Question',
        name: '吃瓜网的内容多久更新一次？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '吃瓜网团队全天候关注娱乐八卦动态，平台内容实时更新，确保你第一时间吃到最新鲜的瓜。',
        },
      },
      {
        '@type': 'Question',
        name: '如何向吃瓜网投稿？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '你可以通过本页面的联系表单向我们提交爆料内容，也可以通过官方社交媒体账号私信投稿。优质内容一经采用，将标注来源。',
        },
      },
      {
        '@type': 'Question',
        name: '吃瓜网的内容是否免费？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '吃瓜网所有内容完全免费，无需注册即可浏览全部文章。你可以通过订阅邮件推送，第一时间获取最新吃瓜内容。',
        },
      },
      {
        '@type': 'Question',
        name: '如果发现不实信息怎么办？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '如果你发现平台上的内容存在不实信息，请通过联系表单或官方邮箱向我们反馈。我们会在核实后及时处理，包括更正或删除相关内容。',
        },
      },
    ],
  };

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于吃瓜网',
    description: '吃瓜网是专注娱乐八卦、明星爆料、热门事件的资讯聚合平台。了解我们的品牌故事、团队背景和联系方式。',
    url: 'https://chiguawang.com/about',
  };

  return (
    <div className="pt-20 pb-14">
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="mb-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        {/* Brand Intro */}
        <section className="mb-16">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block w-16 h-16 flex items-center justify-center bg-primary-500 text-background-50 rounded-2xl text-2xl font-bold font-heading mb-6 mx-auto">
              瓜
            </span>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 font-heading mb-4">
              关于吃瓜网
            </h1>
            <p className="text-lg text-foreground-600 leading-relaxed">
              吃瓜网创建于2024年，是一个专注娱乐八卦、明星爆料、热门事件的资讯聚合平台。"吃瓜"一词源自网络流行语，代表着围观和关注热点事件的态度。我们希望让每一位用户都能在这里第一时间吃到最新鲜的瓜，做最懂吃瓜的人。
            </p>
          </div>
        </section>

        {/* Stats */}
        <section className="mb-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[
              { value: '500+', label: '每日更新文章' },
              { value: '1200万+', label: '月活跃用户' },
              { value: '50万+', label: '订阅用户' },
              { value: '98%', label: '用户满意度' },
            ].map((stat) => (
              <div
                key={stat.label}
                className="text-center p-6 rounded-xl bg-background-100 border border-background-200/70"
              >
                <div className="text-2xl md:text-3xl font-bold text-primary-500 font-heading mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-foreground-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Mission */}
        <section className="mb-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: 'ri-speed-line',
                title: '快速',
                desc: '全天候实时监控娱乐动态，第一时间为你呈现最新鲜的瓜。',
              },
              {
                icon: 'ri-shield-check-line',
                title: '真实',
                desc: '严格的信息审核机制，多重来源交叉验证，拒绝虚假内容。',
              },
              {
                icon: 'ri-group-line',
                title: '有趣',
                desc: '生动的叙事方式和独特的视角，让吃瓜变成一种享受。',
              },
            ].map((item) => (
              <div
                key={item.title}
                className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center"
              >
                <span className="w-14 h-14 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-500 text-2xl mb-4 mx-auto">
                  <i className={item.icon}></i>
                </span>
                <h3 className="text-lg font-bold text-foreground-900 font-heading mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <SectionTitle title="常见问题" subtitle="你可能想了解的一些事情" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                q: '吃瓜网的内容来源是什么？',
                a: '吃瓜网的内容来源于各大社交媒体、娱乐新闻报道以及网友投稿。我们会对信息进行整理和筛选，确保内容的真实性和时效性。',
              },
              {
                q: '吃瓜网的内容多久更新一次？',
                a: '吃瓜网团队全天候关注娱乐八卦动态，平台内容实时更新，确保你第一时间吃到最新鲜的瓜。',
              },
              {
                q: '如何向吃瓜网投稿？',
                a: '你可以通过本页面的联系表单向我们提交爆料内容，也可以通过官方社交媒体账号私信投稿。优质内容一经采用，将标注来源。',
              },
              {
                q: '吃瓜网的内容是否免费？',
                a: '吃瓜网所有内容完全免费，无需注册即可浏览全部文章。你可以通过订阅邮件推送，第一时间获取最新吃瓜内容。',
              },
              {
                q: '如果发现不实信息怎么办？',
                a: '如果你发现平台上的内容存在不实信息，请通过联系表单或官方邮箱向我们反馈。我们会在核实后及时处理。',
              },
              {
                q: '吃瓜网有手机 App 吗？',
                a: '目前吃瓜网主要以网页版形式提供服务，移动端适配已经完成，你可以通过手机浏览器流畅访问。App 版本正在规划中，敬请期待。',
              },
            ].map((faq, index) => (
              <details
                key={index}
                className="group rounded-xl bg-background-100 border border-background-200/70 overflow-hidden cursor-pointer"
              >
                <summary className="flex items-center justify-between px-5 py-4 text-sm font-medium text-foreground-800 list-none">
                  {faq.q}
                  <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                </summary>
                <p className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                  {faq.a}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section>
          <SectionTitle title="联系我们" subtitle="有爆料？有建议？欢迎来信" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <form
                onSubmit={handleContactSubmit}
                data-readdy-form="true"
                className="space-y-4"
              >
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-800 text-sm outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                      placeholder="你的名字"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-800 text-sm outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={contactForm.subject}
                    onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-800 text-sm outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                    placeholder="留言主题"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    name="message"
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-800 text-sm outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all resize-none"
                    placeholder="请输入你的留言内容（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1 text-right">
                    {contactForm.message.length}/500
                  </p>
                </div>
                <button
                  type="submit"
                  disabled={contactStatus === 'loading'}
                  className="px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all disabled:opacity-60 cursor-pointer whitespace-nowrap"
                >
                  {contactStatus === 'loading' ? '提交中...' : '发送留言'}
                </button>

                {contactStatus !== 'idle' && (
                  <p className={`text-sm ${contactStatus === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                    {contactMessage}
                  </p>
                )}
              </form>
            </div>

            <div className="space-y-6">
              <div className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                <h4 className="text-sm font-semibold text-foreground-800 mb-3 font-heading">
                  联系方式
                </h4>
                <div className="space-y-3 text-sm text-foreground-600">
                  <p className="flex items-center gap-2">
                    <i className="ri-mail-line text-primary-500"></i>
                    contact@chiguawang.com
                  </p>
                  <p className="flex items-center gap-2">
                    <i className="ri-weibo-line text-primary-500"></i>
                    @吃瓜网官方微博
                  </p>
                  <p className="flex items-center gap-2">
                    <i className="ri-wechat-line text-primary-500"></i>
                    吃瓜网官方公众号
                  </p>
                </div>
              </div>

              <div className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                <h4 className="text-sm font-semibold text-foreground-800 mb-3 font-heading">
                  工作时间
                </h4>
                <div className="space-y-2 text-sm text-foreground-600">
                  <p>周一至周五：9:00 - 18:00</p>
                  <p>周末及节假日：10:00 - 17:00</p>
                  <p className="text-xs text-foreground-400 mt-2">
                    紧急爆料请通过社交媒体私信，我们会尽快处理
                  </p>
                </div>
              </div>

              <div className="p-5 rounded-xl bg-primary-50 border border-primary-200/70">
                <h4 className="text-sm font-semibold text-primary-700 mb-2 font-heading">
                  有猛料？想爆料？
                </h4>
                <p className="text-sm text-primary-600 mb-3">
                  我们欢迎来自各方的一手爆料。优质爆料一经采用，将支付丰厚稿酬。
                </p>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  了解更多
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}